import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { fmtMoney } from "../lib/api";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBoost() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  useEffect(() => { axios.get(`${API}/public/boost/${slug}`).then(r => setB(r.data)).catch(() => setB(false)); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/public/boost/${slug}/lead`, form);
      setSent(true);
      toast.success("Message sent");
    } catch { toast.error("Send failed"); }
  };

  if (b === false) return <div className="min-h-screen flex items-center justify-center">Listing not found.</div>;
  if (!b) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const shareUrl = `${window.location.origin}/api/share/boost/${slug}`;
  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(b.title);

  return (
    <div className="min-h-screen bg-white" data-testid="public-boost-page">
      <header className="bg-[#e1b7c1] px-8 py-6 flex items-center justify-center">
        <Logo variant="white" height={72} />
      </header>

      {b.images?.[0] && (
        b.listing_url ? (
          <a href={b.listing_url} target="_blank" rel="noopener noreferrer"
             className="block w-full group focus:outline-none focus:ring-2 focus:ring-[#bf858e]"
             data-testid="hero-image-link" aria-label="View full listing details">
            <img src={b.images[0]} alt={b.title}
                 className="w-full h-72 md:h-96 object-cover transition-transform duration-500 group-hover:scale-[1.01]" />
          </a>
        ) : (
          <img src={b.images[0]} alt={b.title} className="w-full h-72 md:h-96 object-cover" />
        )
      )}

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="overline">Featured Listing</div>
        <h1 className="font-heading text-4xl md:text-5xl mt-2">{b.title}</h1>
        <div className="text-sm text-[#050303]/70 mt-2">{b.address}</div>

        <div className="mt-6 flex flex-wrap gap-6 items-center border-y border-[#d1cece] py-4">
          <div className="font-heading text-4xl text-[#bf858e]">{fmtMoney(b.price)}</div>
          <div className="text-sm text-[#050303]/75 flex gap-4">
            {b.beds ? <span><strong>{b.beds}</strong> bd</span> : null}
            {b.baths ? <span><strong>{b.baths}</strong> ba</span> : null}
            {b.sqft ? <span><strong>{b.sqft.toLocaleString()}</strong> sqft</span> : null}
          </div>
        </div>

        {b.description && (
          <div className="mt-6 text-[#050303]/85 whitespace-pre-wrap leading-relaxed">{b.description}</div>
        )}

        <div className="mt-10 core-card p-7" data-testid="lead-form-wrap">
          {sent ? (
            <div data-testid="lead-thanks">
              <h3 className="font-heading text-2xl">Thank you.</h3>
              <p className="text-sm text-[#050303]/70 mt-2">Your CORE Realty and Associates agent will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
              <h3 className="font-heading text-2xl mb-2">Interested? Let's talk.</h3>
              <input required placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="lead-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="lead-email" />
              <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="lead-phone" />
              <textarea rows={3} placeholder="Anything you'd like the agent to know?" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 border border-[#d1cece] rounded-lg" data-testid="lead-message" />
              <div className="flex flex-col sm:flex-row gap-3">
                <button type="submit" className="flex-1 bg-[#050303] text-white pill-btn hover:bg-[#bf858e]" data-testid="lead-submit">Request Info</button>
                {b.listing_url && (
                  <a href={b.listing_url} target="_blank" rel="noopener noreferrer"
                     className="flex-1 pill-btn text-center border-2 border-[#050303] text-[#050303] hover:bg-[#050303] hover:text-white transition-colors"
                     data-testid="lead-listing-details">Listing Details</a>
                )}
              </div>
            </form>
          )}
        </div>

        <div className="mt-8 flex items-center gap-4 text-xs">
          <span className="uppercase tracking-wider text-[#050303]/60">Share:</span>
          {[
            { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, icon: "https://cdn.simpleicons.org/facebook/050303" },
            { name: "X", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}`, icon: "https://cdn.simpleicons.org/x/050303" },
            { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, icon: "https://cdn.simpleicons.org/linkedin/050303" },
            { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${t}%20${u}`, icon: "https://cdn.simpleicons.org/whatsapp/050303" },
            { name: "Email", href: `mailto:?subject=${t}&body=${u}`, icon: "https://cdn.simpleicons.org/gmail/050303" },
          ].map(s => (
            <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`pub-share-${s.name.toLowerCase()}`}>
              <img src={s.icon} alt={s.name} className="w-5 h-5" />
            </a>
          ))}
        </div>
      </div>

      <footer className="bg-[#050303] text-white text-center text-xs py-8 px-6 mt-10">
        <div className="mb-3 flex justify-center"><Logo variant="white" height={40} /></div>
        <div className="opacity-80">CORE Realty and Associates LLC · 2440 Wisteria Drive, Suite 1564, Snellville, GA 30078</div>
        <div className="opacity-80 mt-1">info@corerealtyandassociates.com · 404-317-8526 · corerealtyandassociates.com</div>
      </footer>
    </div>
  );
}
